import { Component } from "@theme/component";

/**
 * Minimal slideshow component that uses CSS native scrolling
 * Only handles dots pagination calculation
 *
 * @extends {Component}
 */
export class Slideshow extends Component {
  async connectedCallback() {
    super.connectedCallback();

    // Wait for slides to be available (for dynamically loaded content)
    await this.#waitForSlides();

    if (!this.isConnected) return;

    // Calculate and update dots pagination on initial load
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (this.isConnected) {
          this.#updateDotsPagination();
        }
      }, { timeout: 1000 });
      } else {
      setTimeout(() => {
        if (this.isConnected) {
          this.#updateDotsPagination();
        }
      }, 100);
    }


      // Set up MutationObserver to watch for dynamically added slides
      this.#setupMutationObserver();

    // Set up click handlers for dots to scroll to slides
    this.addEventListener("click", this.#handleDotClick);

    // Set up scroll tracking to update dots state
    this.#setupScrollTracking();

    // Set up drag scroll functionality
    this.#setupDragScroll();
  }

  /**
   * Handle dot clicks to scroll to corresponding slide
   * @param {Event} event
   */
  #handleDotClick = (event) => {
    if (!(event.target instanceof Element)) return;
    
    // Check for dots
    const dotTarget = event.target.closest('[ref="dots[]"]');
    if (dotTarget) {
      const slideIndex = parseInt(dotTarget.textContent, 10) - 1;
      if (!isNaN(slideIndex)) {
        this.#scrollToSlide(slideIndex);
      }
      return;
    }
    
    // Check for thumbnails
    const thumbnailTarget = event.target.closest('[ref="thumbnails[]"]');
    if (thumbnailTarget) {
      const thumbnails = Array.from(this.querySelectorAll('[ref="thumbnails[]"]'));
      const slideIndex = thumbnails.indexOf(thumbnailTarget);
      if (slideIndex >= 0) {
        this.#scrollToSlide(slideIndex);
      }
      return;
    }
  }

  /**
   * Scroll to a specific slide by index
   * @param {number} slideIndex - The index of the slide to scroll to
   */
  #scrollToSlide(slideIndex) {
    const slides = this.slides;
    if (!slides || !slides[slideIndex]) return;

    const slide = slides[slideIndex];
    if (slide instanceof HTMLElement) {
      // Set flag to prevent scroll handler from interfering
      this.#isScrollingProgrammatically = true;
      
      // Update current slide index immediately
      this.#currentSlideIndex = slideIndex;
      this.#updateDotsState();
      this.#updateThumbnailsState();
      
      slide.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      
      // Wait for smooth scroll to complete, then re-enable scroll tracking
      // Smooth scroll typically takes 300-500ms, so we wait a bit longer
      setTimeout(() => {
        this.#isScrollingProgrammatically = false;
        // Don't recalculate - keep the clicked slide index
        // The scroll handler will take over for future manual scrolls
      }, 600);
    }
  }

  /**
   * Select a slide by index (public method for event delegation)
   * @param {number} index - The index of the slide to select
   * @param {Event} [event] - Optional event object
   * @param {Object} [options] - Optional options
   */
  select(index, event, options = {}) {
    if (typeof index === 'object' && index.id) {
      // Handle select by id: { id: 'some-id' }
      const slides = this.slides;
      const slideIndex = slides.findIndex(slide => 
        slide instanceof HTMLElement && slide.getAttribute('slide-id') === index.id
      );
      if (slideIndex >= 0) {
        this.#scrollToSlide(slideIndex);
      }
    } else if (typeof index === 'number') {
      // Handle select by index
      this.#scrollToSlide(index);
    }
  }

  /**
   * Go to the previous slide (public method for on:click="/previous").
   * Wraps to the last slide when infinite, else no-op at first slide.
   */
  previous() {
    const slides = this.slides;
    if (!slides?.length) return;
    const infinite = this.hasAttribute('infinite');
    let target = this.#currentSlideIndex - 1;
    if (target < 0) {
      if (infinite) target = slides.length - 1;
      else return;
    }
    this.#scrollToSlide(target);
  }

  /**
   * Go to the next slide (public method for on:click="/next").
   * Wraps to the first slide when infinite, else no-op at last slide.
   */
  next() {
    const slides = this.slides;
    if (!slides?.length) return;
    const infinite = this.hasAttribute('infinite');
    let target = this.#currentSlideIndex + 1;
    if (target >= slides.length) {
      if (infinite) target = 0;
      else return;
    }
    this.#scrollToSlide(target);
  }

  /**
   * Update thumbnails aria-selected state based on current slide
   */
  #updateThumbnailsState() {
    const thumbnails = Array.from(this.querySelectorAll('[ref="thumbnails[]"]'));
    if (!thumbnails.length) return;

    thumbnails.forEach((thumbnail, index) => {
      if (thumbnail instanceof HTMLElement) {
        thumbnail.setAttribute('aria-selected', `${index === this.#currentSlideIndex}`);
      }
    });
  }

  /**
   * Wait for slides to be available (handles dynamically loaded content)
   * @returns {Promise<void>}
   */
  #waitForSlides() {
    return new Promise((resolve) => {
      const checkForSlides = () => {
        const slidesContainer = this.querySelector("slideshow-slides");
        if (slidesContainer) {
          const slideElements = Array.from(
            slidesContainer.querySelectorAll("slideshow-slide")
          ).filter((slide) => slide instanceof HTMLElement);
          if (slideElements.length > 0) {
            resolve();
            return true;
          }
        }
        return false;
      };

      // Check immediately
      if (checkForSlides()) return;

      // Use MutationObserver to detect when slides are added
      const observer = new MutationObserver(() => {
        if (checkForSlides()) {
          observer.disconnect();
        }
      });

      observer.observe(this, {
        childList: true,
        subtree: true,
      });

      // Timeout after 5 seconds
      setTimeout(() => {
        observer.disconnect();
        resolve();
      }, 5000);
    });
  }

  /**
   * Sets up MutationObserver to detect when slides are added/removed dynamically
   */
  #setupMutationObserver() {
    const slidesContainer = this.querySelector("slideshow-slides");
    if (!slidesContainer) return;

    // Disconnect existing observer if any
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }

    this.#mutationObserver = new MutationObserver(() => {
      // When slides change, update dots pagination (debounced)
      requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.#debouncedUpdateDotsPagination();
      });
    });

    this.#mutationObserver.observe(slidesContainer, {
      childList: true,
      subtree: false, // Only watch direct children (slideshow-slide elements)
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener("click", this.#handleDotClick);

    const scroller = this.querySelector("slideshow-slides");
    if (scroller) {
      scroller.removeEventListener("scroll", this.#handleScroll);
      scroller.removeEventListener("scrollend", this.#handleScrollEnd);
      
      // Remove mouse drag event listeners
      scroller.removeEventListener("mousedown", this.#handleDragStart);
      scroller.removeEventListener("mousemove", this.#handleDragMove);
      scroller.removeEventListener("mouseup", this.#handleDragEnd);
      scroller.removeEventListener("mouseleave", this.#handleDragEnd);
      scroller.removeEventListener("selectstart", this.#handleSelectStart);
    }

    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
    }

    // Clear any pending dots update
    if (this.#dotsUpdateDebounceTimeout) {
      clearTimeout(this.#dotsUpdateDebounceTimeout);
    }
    if (this.#scrollUpdateTimeout) {
      clearTimeout(this.#scrollUpdateTimeout);
    }

    // Clear cached data
    this.#cachedComputedStyles.clear();
  }

  /**
   * Get slides from DOM
   */
  get slides() {
    const slidesContainer = this.querySelector("slideshow-slides");
    if (!slidesContainer) return [];

    const allSlides = Array.from(slidesContainer.children).filter((slide) => {
      if (
        !(slide instanceof HTMLElement) ||
        slide.tagName !== "SLIDESHOW-SLIDE"
      ) {
        return false;
      }

      // Exclude slides that are inside a nested slideshow-component
      const parentSlideshow = slide.closest("slideshow-component");
      if (parentSlideshow !== this) {
        return false;
      }

      return true;
    });

    return allSlides.filter(
      (slide) => !slide.hasAttribute("hidden") || slide.hasAttribute("reveal")
    );
  }

  /** @type {MutationObserver} */
  #mutationObserver;
  /** @type {number} */
  #lastDotsCount = 0;
  /** @type {number} */
  #dotsUpdateDebounceTimeout;
  /** @type {number} */
  #scrollUpdateTimeout;
  /** @type {number} */
  #currentSlideIndex = 0;
  /** @type {boolean} */
  #isScrollingProgrammatically = false;
  /** @type {Map<string, string>} */
  #cachedComputedStyles = new Map();
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #hasDragMoved = false;
  /** @type {number} */
  #dragStartX = 0;
  /** @type {number} */
  #dragStartScrollLeft = 0;

  /** @type {string} */
  #interactiveDragSelector =
    "wishlist-button, button, input, label, select, textarea, [role='button'], quick-add-component, product-form-component, add-to-cart-component";

  /**
   * @param {Event} event
   * @returns {boolean}
   */
  #isInteractiveDragTarget(event) {
    const target = event.target;
    if (!(target instanceof Element)) return false;
    return !!target.closest(this.#interactiveDragSelector);
  }

  /**
   * Set up scroll tracking to update dots state
   */
  #setupScrollTracking() {
    const scroller = this.querySelector("slideshow-slides");
    if (!scroller) return;

    // Ensure initial state is set to first slide
    this.#currentSlideIndex = 0;
    
    // Set initial dots and thumbnails state immediately if they exist
    const dots = Array.from(this.querySelectorAll('[ref="dots[]"]'));
    if (dots.length > 0) {
      this.#updateDotsState();
    }
    const thumbnails = Array.from(this.querySelectorAll('[ref="thumbnails[]"]'));
    if (thumbnails.length > 0) {
      this.#updateThumbnailsState();
    }

    // Use scrollend event if supported (modern browsers), otherwise debounce scroll
    if ('onscrollend' in window) {
      scroller.addEventListener("scrollend", this.#handleScrollEnd, { passive: true });
    } else {
      // Fallback: debounced scroll event
      scroller.addEventListener("scroll", this.#handleScroll, { passive: true });
    }
    
    // Initial update after a short delay to ensure dots are created
    setTimeout(() => {
      if (this.isConnected) {
        const scroller = this.querySelector("slideshow-slides");
        if (scroller instanceof HTMLElement && scroller.scrollLeft === 0) {
          this.#currentSlideIndex = 0;
          this.#updateDotsState();
          this.#updateThumbnailsState();
        } else {
          this.#updateCurrentSlide();
        }
      }
    }, 200);
  }

  /**
   * Handle scrollend event - update dots after scroll completely stops
   */
  #handleScrollEnd = () => {
    if (this.#isScrollingProgrammatically) return;
    if (this.isConnected) {
      this.#updateCurrentSlide();
    }
  };

  /**
   * Handle scroll events - fallback for browsers without scrollend
   * Uses longer debounce to wait for scroll to fully settle
   */
  #handleScroll = () => {
    if (this.#isScrollingProgrammatically) return;
    
    // Debounce - only update after scroll stops for 150ms
    clearTimeout(this.#scrollUpdateTimeout);
    this.#scrollUpdateTimeout = setTimeout(() => {
      if (this.isConnected) {
        this.#updateCurrentSlide();
      }
    }, 150);
  };

  /**
   * Set up drag scroll functionality for mouse only
   * Touch uses native scroll behavior for smoother experience
   */
  #setupDragScroll() {
    const scroller = this.querySelector("slideshow-slides");
    if (!scroller) return;

    // Touch devices use native horizontal scroll; mouse drag handlers conflict with taps.
    if (window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;

    // Mouse events only - let native touch scroll handle mobile
    scroller.addEventListener("mousedown", this.#handleDragStart);
    scroller.addEventListener("mousemove", this.#handleDragMove);
    scroller.addEventListener("mouseup", this.#handleDragEnd);
    scroller.addEventListener("mouseleave", this.#handleDragEnd);

    // Prevent text selection while dragging
    scroller.addEventListener("selectstart", this.#handleSelectStart);
  }

  /**
   * Prevent text selection during drag
   * @param {Event} event
   */
  #handleSelectStart = (event) => {
    if (this.#isDragging) {
      event.preventDefault();
    }
  };

  /**
   * Handle drag start (mouse only)
   * @param {MouseEvent} event
   */
  #handleDragStart = (event) => {
    if (this.#isInteractiveDragTarget(event)) return;

    const scroller = this.querySelector("slideshow-slides");
    if (!scroller || !(scroller instanceof HTMLElement)) return;

    this.#dragStartX = event.clientX;
    this.#dragStartScrollLeft = scroller.scrollLeft;
    this.#isDragging = true;
    this.#hasDragMoved = false;
    scroller.classList.add("is-dragging");
    document.addEventListener("mouseup", this.#handleDragEnd, { once: true });
    event.preventDefault(); // avoid text selection
  };

  /**
   * Handle drag move (mouse only)
   * @param {MouseEvent} event
   */
  #handleDragMove = (event) => {
    if (!this.#isDragging) return;
    
    const scroller = this.querySelector("slideshow-slides");
    if (!scroller || !(scroller instanceof HTMLElement)) return;

    const deltaX = this.#dragStartX - event.clientX;

    // Once the pointer has moved more than a tiny threshold,
    // we treat the interaction as a drag and suppress the next click.
    if (!this.#hasDragMoved && Math.abs(deltaX) > 3) {
      this.#hasDragMoved = true;

      document.addEventListener(
        "click",
        (clickEvent) => {
          if (
            clickEvent.target instanceof Element &&
            clickEvent.target.closest(this.#interactiveDragSelector)
          ) {
            return;
          }

          if (clickEvent.cancelable) {
            clickEvent.preventDefault();
          }
          // Stop navigation handlers on product cards and other listeners.
          clickEvent.stopPropagation();
          if (typeof clickEvent.stopImmediatePropagation === "function") {
            clickEvent.stopImmediatePropagation();
          }
        },
        { once: true, capture: true }
      );
    }

    scroller.scrollLeft = this.#dragStartScrollLeft + deltaX;
  };

  /**
   * Handle drag end (mouse only)
   * Let CSS scroll-snap handle snapping - dots update via scroll/scrollend event
   * @param {MouseEvent} event
   */
  #handleDragEnd = (event) => {
    if (!this.#isDragging) return;

    const scroller = this.querySelector("slideshow-slides");
    if (!scroller || !(scroller instanceof HTMLElement)) return;

    this.#isDragging = false;
    this.#hasDragMoved = false;
    scroller.classList.remove("is-dragging");
    
    // CSS scroll-snap will handle snapping
    // scrollend/scroll event will update dots after snap completes
  };

  /**
   * Update current slide index based on scroll position
   */
  #updateCurrentSlide() {
    const scroller = this.querySelector("slideshow-slides");
    const slides = this.slides;
    if (!scroller || !(scroller instanceof HTMLElement) || !slides?.length) return;

    const scrollLeft = scroller.scrollLeft;
    const containerWidth = scroller.offsetWidth;
    const scrollWidth = scroller.scrollWidth;
    const maxScrollLeft = scrollWidth - containerWidth;

    // If at the beginning (scrollLeft is 0 or very close), always select first slide
    if (scrollLeft <= 1) {
      if (this.#currentSlideIndex !== 0) {
        this.#currentSlideIndex = 0;
        requestAnimationFrame(() => {
          if (this.isConnected) {
            this.#updateDotsState();
          }
        });
      }
      return;
    }

    // If at or near the end (within 5px of max scroll), always select last slide
    if (maxScrollLeft > 0 && scrollLeft >= maxScrollLeft - 5) {
      const lastIndex = slides.length - 1;
      if (this.#currentSlideIndex !== lastIndex) {
        this.#currentSlideIndex = lastIndex;
        requestAnimationFrame(() => {
          if (this.isConnected) {
            this.#updateDotsState();
          }
        });
      }
      return;
    }

    // Find the slide closest to the scroll position
    let closestIndex = 0;
    let closestDistance = Infinity;

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;

      const slideLeft = slide.offsetLeft;
      const slideWidth = slide.offsetWidth;
      const slideCenter = slideLeft + slideWidth / 2;
      const scrollCenter = scrollLeft + containerWidth / 2;
      const distance = Math.abs(scrollCenter - slideCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    // Update dots and thumbnails if slide changed
    if (closestIndex !== this.#currentSlideIndex) {
      this.#currentSlideIndex = closestIndex;
      requestAnimationFrame(() => {
        if (this.isConnected) {
          this.#updateDotsState();
          this.#updateThumbnailsState();
        }
      });
    }
  }

  /**
   * Update dots aria-selected state based on current slide
   */
  #updateDotsState() {
    const dots = Array.from(this.querySelectorAll('[ref="dots[]"]'));
    if (!dots.length) return;

    // Get the resource list container to determine carousel type
    const resourceListContainer = this.classList.contains(
      "resource-list__carousel"
    )
      ? this
      : this.closest(".resource-list") ||
        this.closest(".resource-list__carousel");

    const isCarousel = resourceListContainer?.classList.contains(
      "resource-list__carousel"
    );
    const isMobile = window.innerWidth < 750;

    // Calculate which dot should be selected
    let selectedDotIndex = this.#currentSlideIndex;
    
    if (isCarousel && !isMobile) {
      // For desktop carousels, map scroll position to a "page" index
      const slides = this.slides;
      const totalSlides = slides.length;
      const totalDots = dots.length;

      if (totalDots < totalSlides && totalDots > 0) {
        const scroller = this.querySelector("slideshow-slides");

        if (scroller instanceof HTMLElement && slides[0] instanceof HTMLElement) {
          // Estimate page width from slide spacing
          let step = slides[0].offsetWidth;
          if (slides[1] instanceof HTMLElement) {
            const diff = slides[1].offsetLeft - slides[0].offsetLeft;
            if (diff > 0) {
              step = diff;
            }
          }

          if (step > 0) {
            const rawPageIndex = Math.round(scroller.scrollLeft / step);
            selectedDotIndex = Math.max(
              0,
              Math.min(rawPageIndex, totalDots - 1)
            );
          } else {
            selectedDotIndex = Math.min(this.#currentSlideIndex, totalDots - 1);
          }
        } else {
          selectedDotIndex = Math.min(this.#currentSlideIndex, totalDots - 1);
        }
      }
    }

    // Update all dots
    dots.forEach((dot, index) => {
      if (dot instanceof HTMLElement) {
        dot.setAttribute("aria-selected", `${index === selectedDotIndex}`);
      }
    });
  }

  /**
   * Debounced version of updateDotsPagination to prevent excessive calls
   */
  #debouncedUpdateDotsPagination() {
    clearTimeout(this.#dotsUpdateDebounceTimeout);
    this.#dotsUpdateDebounceTimeout = setTimeout(() => {
      if (this.isConnected) {
        this.#updateDotsPagination();
      }
    }, 150); // 150ms debounce
  }

  /**
   * Calculate and update dots pagination
   * On desktop carousel: dots = total slides - visible columns
   */
  #updateDotsPagination() {
    const dots = Array.from(this.querySelectorAll('[ref="dots[]"]'));
    const slides = this.slides;
    if (!slides || !slides.length) return;

    requestAnimationFrame(() => {
      if (!this.isConnected) return;

      // Check if this slideshow-component itself has the carousel class, or find parent
      const resourceListContainer = this.classList.contains(
        "resource-list__carousel"
      )
        ? this
        : this.closest(".resource-list") ||
          this.closest(".resource-list__carousel");
      if (!resourceListContainer) return;

      const isCarousel = resourceListContainer.classList.contains(
        "resource-list__carousel"
      );
      const isGrid = resourceListContainer.classList.contains(
        "resource-list--grid"
      );

      let totalPages;
      let totalPagesDesktop;

      const isMobile = window.innerWidth < 750;
      const totalSlides = slides.length;

      if (isCarousel) {
        if (isMobile) {
          // Mobile: one dot per slide
          totalPages = totalSlides;
          totalPagesDesktop = totalSlides;
        } else {
          // Desktop: dots = total slides - visible columns + 1
          // Get visible columns from CSS variable (with caching)
          const containerElement = /** @type {HTMLElement} */ (
            resourceListContainer
          );
          const cacheKey = `${containerElement.id || 'default'}-column-count`;
          let columnCountVar = this.#cachedComputedStyles.get(cacheKey);
          
          if (!columnCountVar) {
            columnCountVar = containerElement.style.getPropertyValue("--column-count") || "4";

            // If not found on the container, check computed style (cache result)
            if (columnCountVar === "4") {
              const computedStyle = getComputedStyle(containerElement);
              columnCountVar = computedStyle.getPropertyValue("--column-count")?.trim() || "4";
            }

            // Cache the result
            this.#cachedComputedStyles.set(cacheKey, columnCountVar);
          }

          let visibleColumns = 4; // Default fallback changed to 4 for carousels
          if (columnCountVar) {
            visibleColumns = Math.max(1, parseInt(columnCountVar, 10));
          }

          // Desktop formula: total slides - visible columns + 1
          // Example: 19 slides, 4 visible = 16 dots (positions 0-15, where position 15 shows slides 15-18)
          totalPagesDesktop = Math.max(1, totalSlides - visibleColumns + 1);
          totalPages = totalPagesDesktop;
        }
      } else if (isGrid) {
        // For grid: calculate based on columns (with caching)
        const containerElement = /** @type {HTMLElement} */ (
          resourceListContainer
        );
        const cacheKeyDesktop = `${containerElement.id || 'default'}-column-count`;
        const cacheKeyMobile = `${containerElement.id || 'default'}-column-count-mobile`;
        
        let columnCountVar = this.#cachedComputedStyles.get(cacheKeyDesktop);
        let columnCountMobileVar = this.#cachedComputedStyles.get(cacheKeyMobile);
        
        if (!columnCountVar || !columnCountMobileVar) {
          // Try inline style first
          columnCountVar = columnCountVar || containerElement.style.getPropertyValue("--column-count");
          columnCountMobileVar = columnCountMobileVar || containerElement.style.getPropertyValue("--column-count-mobile");
          
          // If not found, check computed style (single call)
        if (!columnCountVar || !columnCountMobileVar) {
          const computedStyle = getComputedStyle(containerElement);
          if (!columnCountVar) {
            columnCountVar = computedStyle.getPropertyValue("--column-count")?.trim();
          }
          if (!columnCountMobileVar) {
            columnCountMobileVar = computedStyle.getPropertyValue("--column-count-mobile")?.trim();
          }
          }
          
          // Cache results
          if (columnCountVar) this.#cachedComputedStyles.set(cacheKeyDesktop, columnCountVar);
          if (columnCountMobileVar) this.#cachedComputedStyles.set(cacheKeyMobile, columnCountMobileVar);
        }

        const columnsDesktop = Math.max(
          1,
          columnCountVar ? parseInt(columnCountVar, 10) : 1
        );
        const columnsMobile = Math.max(
          1,
          columnCountMobileVar ? parseInt(columnCountMobileVar, 10) : 1
        );
        totalPagesDesktop = Math.ceil(totalSlides / columnsDesktop);
        totalPages = Math.ceil(totalSlides / columnsMobile);
      } else {
        // Default: one dot per slide
        totalPages = totalSlides;
        totalPagesDesktop = totalSlides;
      }

      const currentDotsCount = dots.length;
      const dotsContainer = this.querySelector(".slideshow-controls__dots");
      if (!dotsContainer) return;

      // Hide dots container if there's only one page
      if (totalPages === 1) {
        if (dotsContainer instanceof HTMLElement) {
          dotsContainer.style.display = "none";
        }
        // Clear dots and update last count
        dotsContainer.innerHTML = "";
        this.#lastDotsCount = totalPages;
        return;
      }

      // Show dots container if it was previously hidden
      if (dotsContainer instanceof HTMLElement) {
        dotsContainer.style.display = "";
      }

      // Update dots if count changed or if this is the first time (lastDotsCount is 0)
      if (totalPages !== currentDotsCount || this.#lastDotsCount === 0) {
        // Use DocumentFragment to batch DOM updates
        const fragment = document.createDocumentFragment();

        // Create new dots based on calculated total pages
        for (let i = 0; i < totalPages; i++) {
          const li = document.createElement("li");
          if (isGrid && i >= totalPagesDesktop) {
            li.classList.add("hide-desktop");
          }

          const button = document.createElement("button");
          button.className = "slideshow-control button button-unstyled";
          button.style.setProperty("animation-timeline", `--slide-${i + 1}`);
          button.setAttribute("aria-label", `Slide ${i + 1} of ${totalPages}`);
          button.setAttribute("on:click", `/select/${i}`);
          button.setAttribute("ref", "dots[]");
          // Set initial aria-selected state (first dot selected by default)
          button.setAttribute("aria-selected", `${i === 0}`);
          button.textContent = String(i + 1);

          li.appendChild(button);
          fragment.appendChild(li);
        }

        // Single DOM update instead of multiple appends
        dotsContainer.innerHTML = "";
        dotsContainer.appendChild(fragment);

        // Store the last calculated count to prevent unnecessary updates
        const wasInitialLoad = this.#lastDotsCount === 0;
        this.#lastDotsCount = totalPages;

        // Ensure first dot is selected on initial load
        if (wasInitialLoad) {
          this.#currentSlideIndex = 0;
        }

        // Update dots and thumbnails state after creating new dots
        requestAnimationFrame(() => {
          if (this.isConnected) {
            this.#updateDotsState();
            this.#updateThumbnailsState();
          }
        });
      } else {
        // Count hasn't changed, but ensure aria-selected is correct
        this.#updateDotsState();
        this.#updateThumbnailsState();
      }
    });
  }
}

// Register the custom element
if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", Slideshow);
}
